'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport } from 'ai';
import type { CannyChatSummary } from './CannySidebar';
import type { MentionItem } from './CannyMentionMenu';
import { useI18n } from '@/contexts/I18nContext';

function authHeaders(): Record<string, string> {
  const token = typeof window === 'undefined' ? null : localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Вся работа со списком чатов и отправкой сообщений.
 *
 * Вынесено из `CannyShell`, чтобы тот остался раскладкой: смешивать вёрстку
 * с загрузкой, созданием, удалением и стримингом в одном файле — верный путь
 * к компоненту, который никто не решается тронуть.
 */
export function useCannyChat() {
  const { locale } = useI18n();
  const localeRef = useRef(locale);
  localeRef.current = locale;
  const [chats, setChats] = useState<CannyChatSummary[]>([]);
  const [chatId, setChatId] = useState<string | null>(null);
  const [mentioned, setMentioned] = useState<MentionItem[]>([]);
  /** Текст при упёршемся лимите демо — приглашение зарегистрироваться. */
  const [limitMessage, setLimitMessage] = useState<string | null>(null);

  // chatId и меншены нужны внутри transport, который создаётся один раз:
  // держим их в ref, иначе замыкание навсегда запомнит начальные значения.
  const chatIdRef = useRef<string | null>(null);
  const mentionedRef = useRef<MentionItem[]>([]);
  // Картинки текущего сообщения — тот же приём с ref, что и для chatId.
  const imageUrlsRef = useRef<string[]>([]);
  chatIdRef.current = chatId;
  mentionedRef.current = mentioned;

  const { messages, setMessages, sendMessage, status } = useChat({
    transport: new DefaultChatTransport({
      api: '/api/canny',
      headers: authHeaders,
      body: () => ({
        chatId: chatIdRef.current,
        mentions: mentionedRef.current,
        imageUrls: imageUrlsRef.current,
        locale: localeRef.current,
      }),
    }),
    onError: (error) => {
      // 429 от демо — не поломка, а развилка: показываем приглашение
      // зарегистрироваться вместо красной ошибки.
      const text = error instanceof Error ? error.message : String(error);
      try {
        const parsed = JSON.parse(text);
        if (parsed?.needsSignup && parsed?.error) {
          setLimitMessage(parsed.error);
          return;
        }
      } catch {
        // не JSON — значит обычная ошибка, пусть всплывает как есть
      }
    },
  });

  const loadChats = useCallback(async () => {
    const response = await fetch('/api/canny/chats', { headers: authHeaders() });
    if (!response.ok) return;
    const data = await response.json();
    setChats(data.chats ?? []);
  }, []);

  /**
   * Активный чат живёт в адресе страницы.
   *
   * Без этого F5 открывал пустой «Новый чат», и разговор выглядел потерянным,
   * хотя лежал в базе. Заодно ссылкой на чат можно поделиться.
   */
  const rememberInUrl = useCallback((id: string | null) => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (id) url.searchParams.set('c', id);
    else url.searchParams.delete('c');
    window.history.replaceState(null, '', url.toString());
  }, []);

  const createChat = useCallback(async (): Promise<string | null> => {
    const response = await fetch('/api/canny/chats', { method: 'POST', headers: authHeaders() });
    if (!response.ok) return null;
    const data = await response.json();
    setChatId(data.id);
    chatIdRef.current = data.id;
    rememberInUrl(data.id);
    setMessages([]);
    await loadChats();
    return data.id as string;
  }, [loadChats, rememberInUrl, setMessages]);

  const openChat = useCallback(
    async (id: string) => {
      setChatId(id);
      chatIdRef.current = id;
      rememberInUrl(id);
      const response = await fetch(`/api/canny/chats/${id}`, { headers: authHeaders() });
      if (!response.ok) {
        // Чат удалён или чужой — не оставляем в адресе ссылку в никуда.
        rememberInUrl(null);
        return;
      }
      const data = await response.json();
      setMessages(
        (data.messages ?? []).map(
          (message: { id: string; role: string; content: string; parts?: unknown[] }) => ({
            id: message.id,
            role: message.role,
            // Части восстанавливаем как есть — в них живут карточки брони,
            // генерации и приложенные картинки. Схлопывание в один текст
            // стирало их при каждой перезагрузке страницы.
            parts:
              Array.isArray(message.parts) && message.parts.length > 0
                ? message.parts
                : [{ type: 'text' as const, text: message.content }],
          }),
        ),
      );
    },
    [rememberInUrl, setMessages],
  );

  const removeChat = useCallback(
    async (id: string) => {
      await fetch(`/api/canny/chats/${id}`, { method: 'DELETE', headers: authHeaders() });
      if (chatIdRef.current === id) {
        setChatId(null);
        chatIdRef.current = null;
        rememberInUrl(null);
        setMessages([]);
      }
      await loadChats();
    },
    [loadChats, setMessages],
  );

  const send = useCallback(
    async (text: string, imageUrls: string[] = []) => {
      const trimmed = text.trim();
      // Одна картинка без текста — тоже осмысленное сообщение: «вот такой пост».
      if (!trimmed && imageUrls.length === 0) return;
      imageUrlsRef.current = imageUrls;
      // Чат заводится лениво — при первом сообщении, а не при открытии страницы,
      // иначе список засоряется пустыми «Новый чат».
      if (!chatIdRef.current) {
        const created = await createChat();
        if (!created) return;
      }
      // Картинки идут ЧАСТЯМИ сообщения, а не только в теле запроса: иначе
      // модель их видит, а клиент в переписке — нет, и выглядит это так,
      // будто вложение потерялось.
      await sendMessage({
        parts: [
          ...imageUrls.map((url) => ({
            type: 'file' as const,
            url,
            mediaType: 'image/*',
          })),
          { type: 'text' as const, text: trimmed || 'Посмотри на эту картинку' },
        ],
      });
      // Меншены и картинки относятся к одному сообщению, а не к чату целиком.
      setMentioned([]);
      imageUrlsRef.current = [];
      void loadChats();
    },
    [createChat, loadChats, sendMessage],
  );

  /** Перенос демо-разговора в аккаунт. Вызывается сразу после входа. */
  const claimAnonymous = useCallback(async () => {
    const response = await fetch('/api/canny/claim', { method: 'POST', headers: authHeaders() });
    if (!response.ok) return;
    const data = await response.json();
    if (data?.claimed?.chats > 0) await loadChats();
  }, [loadChats]);

  return {
    chats,
    chatId,
    limitMessage,
    claimAnonymous,
    messages,
    status,
    busy: status === 'submitted' || status === 'streaming',
    hasMessages: messages.length > 0,
    mentioned,
    addMention: (item: MentionItem) => setMentioned((current) => [...current, item]),
    loadChats,
    createChat,
    openChat,
    removeChat,
    send,
  };
}

/** Ролики библиотеки для `@`-меню. Промах не должен ломать разговор. */
export function useCannyVideos(enabled: boolean) {
  const [videos, setVideos] = useState<Array<{ id: string; title: string }>>([]);

  useEffect(() => {
    if (!enabled) return;
    void (async () => {
      try {
        const response = await fetch('/api/videos', { headers: authHeaders() });
        if (!response.ok) return;
        const data = await response.json();
        const list: Array<{ _id?: string; title?: string }> = data?.videos ?? [];
        setVideos(
          list
            .filter((video) => video?._id)
            .map((video) => ({ id: String(video._id), title: video.title || 'Без названия' }))
            .slice(0, 50),
        );
      } catch {
        // молча: меню меншенов не критично для разговора
      }
    })();
  }, [enabled]);

  return videos;
}
